/**
 * Turns whatever `runAspWorkflow` rejects with (an axios error, a fetch-level failure,
 * or a plain Error from the auth interceptors) into a user-facing description for the
 * ASP Approved modal.
 *
 * The Enterprise API answers failures with RFC 7807 ProblemDetails. `ApiProblemFactory`
 * flattens extensions to the top level, so `code` and `traceId` sit beside `title`/`detail`.
 * `GlobalExceptionHandler` maps unhandled exceptions: infrastructure failures → 503,
 * domain/validation → 400, a dropped client connection → 499. Anything without a
 * response at all is a timeout or a network/proxy drop on our side.
 */

export interface AspWorkflowErrorInfo {
    /** Short heading for the alert. */
    title: string;
    /** What happened, in plain language. */
    detail: string;
    /** What the user should do next. */
    nextStep: string;
    /** HTTP status if the server answered. */
    status?: number;
    /** Machine-readable error code from ProblemDetails (e.g. ASP.Workflow.Validation.StaffNotFound). */
    code?: string;
    /** Correlation id the API logs under; quote it to support. */
    traceId?: string;
}

const RETRY = 'Please try this request again later.';
const CONTACT = 'If the problem continues, contact IT support and include the details below.';

/** Shape-only view of an axios error; kept loose so non-axios rejections classify too. */
interface ErrorLike {
    code?: string;
    message?: string;
    response?: {
        status?: number;
        data?: {
            title?: string;
            detail?: string;
            code?: string;
            traceId?: string;
            errors?: Record<string, string[]>;
        } | string;
    };
    request?: unknown;
}

const isTimeoutCode = (code?: string) =>
    code === 'ECONNABORTED' || code === 'ETIMEDOUT';

const isNetworkCode = (code?: string) =>
    code === 'ERR_NETWORK' || code === 'ERR_CANCELED';

export function describeAspWorkflowError(err: unknown): AspWorkflowErrorInfo {
    const e = (err ?? {}) as ErrorLike;
    const status = e.response?.status;
    const data = typeof e.response?.data === 'object' && e.response?.data !== null ? e.response.data : undefined;
    const serverTitle = data?.title?.trim() || undefined;
    const serverDetail = data?.detail?.trim() || undefined;
    const code = data?.code;
    const traceId = data?.traceId;

    // No response at all: the request never completed.
    if (status === undefined) {
        if (isTimeoutCode(e.code)) {
            return {
                title: 'Request timed out',
                detail: 'The ASP workflow did not respond in time. It may still be running on the server.',
                nextStep: `Wait a minute, then check the staffing process before running it again. ${RETRY}`,
            };
        }
        if (isNetworkCode(e.code) || e.request) {
            return {
                title: 'Connection lost',
                detail: 'The request could not reach the server, or the connection was dropped before it answered. If the workflow ran for a long time, a proxy may have closed the connection.',
                nextStep: `Check your network connection. ${RETRY}`,
            };
        }
        // Rejections from the auth interceptors arrive as plain Errors with no request/response.
        if (/authentication|token/i.test(e.message ?? '')) {
            return {
                title: 'Sign-in required',
                detail: 'Your session needs to be refreshed before the ASP workflow can run.',
                nextStep: 'Reload the page to sign in again, then run the workflow.',
            };
        }
        return {
            title: 'Unexpected error',
            detail: e.message?.trim() || 'Something went wrong running the ASP workflow.',
            nextStep: `${RETRY} ${CONTACT}`,
        };
    }

    const base = { status, code, traceId };

    switch (true) {
        case status === 400: {
            const validation = data?.errors
                ? Object.values(data.errors).flat().join(' ')
                : undefined;
            return {
                ...base,
                title: serverTitle ?? 'Request rejected',
                detail: serverDetail ?? validation ?? 'The server rejected the request as invalid.',
                nextStep: code?.endsWith('StaffNotFound')
                    ? 'Confirm the staff member has an active account, then run the workflow again.'
                    : 'Check the staff member and client on this process, then run the workflow again.',
            };
        }
        case status === 401:
            return {
                ...base,
                title: 'Session expired',
                detail: serverDetail ?? 'Your sign-in is no longer valid.',
                nextStep: 'Reload the page to sign in again, then run the workflow.',
            };
        case status === 403:
            return {
                ...base,
                title: 'Not permitted',
                detail: serverDetail ?? 'Your account is not allowed to run the ASP workflow.',
                nextStep: 'Ask a staffing dashboard administrator to run it, or request access.',
            };
        case status === 404:
            return {
                ...base,
                title: serverTitle ?? 'Not found',
                detail: serverDetail ?? 'The staff member, client or process could not be found.',
                nextStep: 'Refresh the dashboard and check the process still exists, then try again.',
            };
        case status === 409:
            return {
                ...base,
                title: serverTitle ?? 'Conflict',
                detail: serverDetail ?? 'The workflow conflicts with the current state of this process.',
                nextStep: 'Refresh the dashboard to see the latest state before running it again.',
            };
        case status === 429:
            return {
                ...base,
                title: 'Too many requests',
                detail: serverDetail ?? 'The server is rate-limiting requests from this session.',
                nextStep: 'Wait a minute, then run the workflow again.',
            };
        case status === 499:
            return {
                ...base,
                title: 'Request cancelled',
                detail: serverDetail ?? 'The connection closed before the workflow finished.',
                nextStep: RETRY,
            };
        case status === 502 || status === 504:
            return {
                ...base,
                title: 'Gateway timeout',
                detail: 'The API gateway gave up waiting for the ASP workflow. The workflow may still be running on the server.',
                nextStep: `Wait a minute, then check the staffing process before running it again. ${RETRY}`,
            };
        case status === 503:
            return {
                ...base,
                title: 'Service unavailable',
                detail: serverDetail ?? 'A system the ASP workflow depends on (SharePoint, database or PBS Live) did not respond.',
                nextStep: `${RETRY} ${CONTACT}`,
            };
        case status >= 500:
            return {
                ...base,
                title: serverTitle ?? 'Server error',
                detail: serverDetail ?? 'The ASP workflow failed on the server.',
                nextStep: `${RETRY} ${CONTACT}`,
            };
        default:
            return {
                ...base,
                title: serverTitle ?? 'Error',
                detail: serverDetail ?? 'Something went wrong running the ASP workflow.',
                nextStep: `${RETRY} ${CONTACT}`,
            };
    }
}
